// @ts-strict-ignore
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import {
    colorForAutomationStatus,
    displayTextForAutomationStatus,
    displayTextForMonitoringMetric,
    fullDisplayTextForMonitoringMetric,
    getRolloutChecklistItemsOwners,
} from '../../helpers/displayHelpers';
import { displayTextForHealthMetricPrecondition, renamedMetricTooltip } from './monitoringHelpers';
import { createAppSettingsAutomationsRouteWithAutomation } from '../../helpers/routeHelpers';
import { RootState } from '../../reducers/reducers';
import {
    selectAppIntegrations,
    selectAppTimeFormatSetting,
    selectAutomationById,
    selectEffectiveTimezone,
    selectMonitoringMetricsById,
    selectOrgGroups,
    selectOrgUsers,
    selectStabilityIntegrations,
} from '../../selectors/selectors';
import {
    Automation,
    AutomationHaltUnstableRelease,
    AutomationPrepareRollbackRelease,
    AutomationPromoteStablePhasedRelease,
    AutomationStatus,
    AutomationType,
    ChecklistItem,
    IntegrationInstallationId,
    MonitoringAutomation,
    MonitoringAutomationChecklistItem,
    MonitoringAutomationCondition,
    MonitoringAutomationConditionType,
    MonitoringAutomationMetric,
    MonitoringMetricHealthStatus,
    SubscriptionFeatureType,
    ChecklistItemStatusType,
    AutomationRun,
} from '../../types';
import { ReleaseComponentProps, ReleaseRouteComponentMatchParams } from '../../typescriptHelpers';
import { antGrey, colorWithAlpha, runwayColors, rwColors, tagStyle } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import {
    ThunderboltFilled,
    ClockCircleFilled,
    CheckCircleFilled,
    CloseCircleFilled,
    SettingOutlined,
    CaretRightOutlined,
} from '@ant-design/icons';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat, formatDateWithTimezone } from '../../timeHelpers';
import Tooltip from 'antd/lib/tooltip';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import { iconForAutomationStatus } from '../automations/AutomationTab';
import Collapse from 'antd/lib/collapse';

interface AutomationConditionThresholds {
    adoptionThreshold?: number;
    adoptionThresholds?: Record<IntegrationInstallationId, number>;
    phasedReleaseDayThreshold?: number;
    stagedRolloutPercentageThreshold?: number;
}

interface MonitoringAutomationProps extends ReleaseComponentProps {
    monitoringAutomations: MonitoringAutomation[];
    shouldShowDisabledUI?: boolean;
    checklistItems?: ChecklistItem[];
    automationRuns: AutomationRun[];
}

const MonitoringAutomations: React.FC<MonitoringAutomationProps> = (props: MonitoringAutomationProps) => {
    const { orgIdParam, appIdParam } = useParams<ReleaseRouteComponentMatchParams>();

    const monitoringAutomationsFromSettings = useSelector((state: RootState) => {
        return (
            props.monitoringAutomations?.reduce(
                (acc, monitoringAutomation) => {
                    const automation = selectAutomationById(state, props.appId, monitoringAutomation.id);

                    if (automation) {
                        acc[monitoringAutomation.id] = automation;
                    }

                    return acc;
                },
                {} as { [key: string]: Automation },
            ) ?? []
        );
    });

    const automationRunsById = React.useMemo(() => {
        const automationRuns = props.automationRuns ?? [];
        return automationRuns.reduce((acc, run) => {
            acc[run.id] = run;
            return acc;
        }, {});
    }, [props.automationRuns]);

    const monitoringMetricsById = useSelector((state: RootState) => selectMonitoringMetricsById(state, props.appId));
    const stabilityIntegrations = useSelector((state: RootState) => selectStabilityIntegrations(state, props.appId));
    const stabilityIntegrationNameByInstallationId = React.useMemo(() => {
        return stabilityIntegrations.reduce<Record<string, string>>((acc, integration) => {
            acc[integration.installationId] = integration.name;
            return acc;
        }, {});
    }, [stabilityIntegrations]);
    const appIntegrations = useSelector((state: RootState) => selectAppIntegrations(state, props.appId));
    const integrationNameByInstallationId = React.useMemo(() => {
        return appIntegrations.reduce<Record<string, string>>((acc, integration) => {
            acc[integration.installationId] = integration.name;
            return acc;
        }, {});
    }, [appIntegrations]);

    const shouldSuffixIntegrationNameByInstallationId = React.useMemo(() => {
        const capabilityCounts: Record<string, number> = {};
        for (const integration of appIntegrations) {
            for (const capability of integration.capabilities ?? []) {
                capabilityCounts[capability] = (capabilityCounts[capability] ?? 0) + 1;
            }
        }
        return appIntegrations.reduce<Record<string, boolean>>((acc, integration) => {
            const hasAmbiguousCapability = (integration.capabilities ?? []).some(
                (capability) => (capabilityCounts[capability] ?? 0) > 1,
            );
            acc[integration.installationId] = hasAmbiguousCapability;
            return acc;
        }, {});
    }, [appIntegrations]);
    // TODO: remove reference to orgUsers whenever possible
    const orgUsers = useSelector((state: RootState) => selectOrgUsers(state, props.orgId));
    const orgGroups = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));

    const statusTagForAutomationStatus = (
        enabled: boolean,
        automationType: AutomationType,
        status: AutomationStatus,
        statusMessage?: string,
        triggeredAt?: string,
        discardedAt?: string,
    ) => {
        let text = displayTextForAutomationStatus(status);
        let color = colorForAutomationStatus(status, false);
        let tooltipText = null;

        if (statusMessage) {
            tooltipText = statusMessage;
        }

        if (!enabled) {
            text = 'Disabled';
            color = antGrey.grey5;
        } else {
            switch (status) {
                case AutomationStatus.Active:
                    text = 'Active';
                    color = runwayColors.primary;
                    break;
                case AutomationStatus.Success: {
                    switch (automationType) {
                        case AutomationType.AutoHaltUnstablePhasedReleases: {
                            color = runwayColors.statusRed;
                            text = 'triggered';
                            tooltipText = `Triggered on: ${formatDateWithTimezone(triggeredAt, 'fullYearAtTime', effectiveTimezone, use24HourTimeFormat)}`;
                            break;
                        }
                        case AutomationType.AutoPromoteStablePhasedReleases: {
                            color = runwayColors.statusGreen;
                            text = 'triggered';
                            tooltipText = `Triggered on: ${formatDateWithTimezone(triggeredAt, 'fullYearAtTime', effectiveTimezone, use24HourTimeFormat)}`;
                            break;
                        }
                        case AutomationType.AutoPrepareRollbackRelease: {
                            color = runwayColors.primary;
                            text = 'Prepared';
                            tooltipText = 'Rollback has been prepared.';
                            break;
                        }
                        default:
                            break;
                    }
                    break;
                }
                default:
                    break;
            }

            if (automationType === AutomationType.AutoPrepareRollbackRelease && !!discardedAt) {
                color = runwayColors.statusGrey;
                text = 'Discarded';
            }
        }

        const tag = (
            <Tag style={{ ...tagStyle, color: color, borderColor: color }} color={runwayColors.white}>
                {text}
            </Tag>
        );

        if (tooltipText) {
            return <Tooltip title={tooltipText}>{tag}</Tooltip>;
        }

        return tag;
    };

    const conditionTextStyle: React.CSSProperties = { color: antGrey.grey7, fontSize: 13 };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                width: '100%',
                padding: 20,
                borderRadius: 8,
                border: `1px solid ${antGrey.grey5}`,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts} requiresTooltip>
                    <Text style={{ color: antGrey.grey8, fontWeight: 600 }}>Automations</Text>
                </PlanRestrictedFeatureWrapper>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
                {props.monitoringAutomations?.map((automation, index) => {
                    const automationFromSettings = monitoringAutomationsFromSettings[automation.id] as Automation;
                    const automationRun = automationRunsById[automation.id];

                    if (!automationFromSettings || !automationRun) {
                        return null;
                    }

                    const description = automationFromSettings.name;

                    const automationLink = createAppSettingsAutomationsRouteWithAutomation(
                        orgIdParam,
                        appIdParam,
                        automation.id,
                    );

                    const status = automationRun.status;
                    const statusMessage = automationRun.statusMessage;

                    const shouldShowMetricsData =
                        automationFromSettings.enabled &&
                        status !== AutomationStatus.Skipped &&
                        automation.id !== AutomationType.AutoPrepareRollbackRelease;

                    const automationEnabledAndReleaseNotSkipped =
                        automationFromSettings.enabled && status !== AutomationStatus.Skipped;

                    const shouldShowAutomationSteps =
                        automationFromSettings.enabled &&
                        automation.id === AutomationType.AutoPrepareRollbackRelease &&
                        !automation.discardedAt;

                    const shouldShowRollbackDiscardedText =
                        automation.id === AutomationType.AutoPrepareRollbackRelease && !!automation.discardedAt;

                    const isPrepareRollback = automationFromSettings.type === AutomationType.AutoPrepareRollbackRelease;
                    const automationThresholds: AutomationConditionThresholds = {
                        adoptionThreshold:
                            isPrepareRollback ?
                                (automationFromSettings as AutomationPrepareRollbackRelease).data
                                    ?.abandonRollbackAdoptionThreshold
                            :   (
                                    automationFromSettings as
                                        | AutomationPromoteStablePhasedRelease
                                        | AutomationHaltUnstableRelease
                                ).data?.adoptionThreshold,
                        adoptionThresholds:
                            isPrepareRollback ?
                                (automationFromSettings as AutomationPrepareRollbackRelease).data
                                    ?.abandonRollbackAdoptionThresholds
                            :   (
                                    automationFromSettings as
                                        | AutomationPromoteStablePhasedRelease
                                        | AutomationHaltUnstableRelease
                                ).data?.adoptionThresholds,
                        phasedReleaseDayThreshold: (automationFromSettings as AutomationPromoteStablePhasedRelease)
                            ?.data?.phasedReleaseDayThreshold,
                        stagedRolloutPercentageThreshold: (
                            automationFromSettings as
                                | AutomationPromoteStablePhasedRelease
                                | AutomationHaltUnstableRelease
                        )?.data?.stagedRolloutPercentageThreshold,
                    };

                    const conditionGroups = automation.conditionGroups ?? [];
                    const hasConditionGroups = conditionGroups.length > 0;
                    const thresholdsForConditionGroup = (groupId: string): AutomationConditionThresholds => {
                        const settingsGroup = (
                            automationFromSettings as AutomationHaltUnstableRelease
                        ).data?.conditionGroups?.find((group) => group.id === groupId);

                        return {
                            adoptionThresholds: settingsGroup?.adoptionThresholds,
                            phasedReleaseDayThreshold: settingsGroup?.phasedReleaseDayThreshold,
                            stagedRolloutPercentageThreshold: settingsGroup?.stagedRolloutPercentageThreshold,
                        };
                    };

                    const metricsWithThresholds = (metrics?: MonitoringAutomationMetric[]) =>
                        (metrics ?? []).filter((metric) => monitoringMetricsById[metric.id]?.thresholds?.length > 0);

                    const renderHealthMetrics = (metrics?: MonitoringAutomationMetric[]) => (
                        <>
                            {(metrics ?? []).length === 0 && (
                                <Text style={conditionTextStyle}>
                                    No health metrics set. Go to this automation&apos;s settings to configure metrics.
                                </Text>
                            )}
                            {(metrics ?? []).map((metric, metricIndex) => {
                                const monitoringMetric = monitoringMetricsById[metric.id];
                                // Append the integration name only when there's actual ambiguity
                                const installationIds = monitoringMetric?.integrationInstallationIds ?? [];
                                let integrationLabelSuffix = '';
                                if (installationIds.length === 1) {
                                    const installationId = installationIds[0];
                                    if (shouldSuffixIntegrationNameByInstallationId[installationId]) {
                                        const integrationName = integrationNameByInstallationId[installationId];
                                        if (integrationName) {
                                            integrationLabelSuffix = ` (${integrationName})`;
                                        }
                                    }
                                }
                                const metricLabel =
                                    monitoringMetric && fullDisplayTextForMonitoringMetric(monitoringMetric);

                                return (
                                    <Space key={metricIndex}>
                                        {iconForMonitoringMetricStatus(metric.status)}
                                        <Tooltip
                                            title={renamedMetricTooltip(
                                                monitoringMetric?.customDisplayName,
                                                displayTextForMonitoringMetric(
                                                    {
                                                        ...monitoringMetric,
                                                        customDisplayName: undefined,
                                                    },
                                                    true,
                                                ),
                                            )}
                                        >
                                            <Text
                                                style={{
                                                    ...conditionTextStyle,
                                                    color:
                                                        metric.status === MonitoringMetricHealthStatus.Unhealthy ?
                                                            runwayColors.statusRed
                                                        :   conditionTextStyle.color,
                                                }}
                                            >
                                                {metricLabel}
                                                {integrationLabelSuffix}
                                            </Text>
                                        </Tooltip>
                                    </Space>
                                );
                            })}
                        </>
                    );

                    const renderConditions = (
                        conditions: MonitoringAutomationCondition[] | undefined,
                        thresholds: AutomationConditionThresholds,
                        checklistItems?: MonitoringAutomationChecklistItem[],
                    ) => (
                        <>
                            {(conditions ?? []).length === 0 && checklistItems?.length === 0 && (
                                <Text style={conditionTextStyle}>
                                    No conditions set. Go to this automation&apos;s settings to configure conditions.
                                </Text>
                            )}
                            {(conditions ?? []).map((condition, conditionIndex) => {
                                let text = '';
                                if (condition.type === MonitoringAutomationConditionType.Adoption) {
                                    const installationId = condition.integrationInstallationId;
                                    const threshold =
                                        condition.threshold ??
                                        (installationId ?
                                            thresholds.adoptionThresholds?.[installationId]
                                        :   thresholds.adoptionThreshold);
                                    if (!threshold) {
                                        text = '';
                                    } else if (installationId) {
                                        const integrationName =
                                            stabilityIntegrationNameByInstallationId[installationId];
                                        text =
                                            integrationName ?
                                                `${threshold}% session adoption (${integrationName})`
                                            :   `${threshold}% session adoption`;
                                    } else {
                                        // Legacy single-threshold path
                                        const stabilityNames = stabilityIntegrations.map((i) => i.name).filter(Boolean);
                                        const suffix =
                                            stabilityNames.length > 0 ? ` (${stabilityNames.join(', ')})` : '';
                                        text = `${threshold}% session adoption${suffix}`;
                                    }
                                } else if (condition.type === MonitoringAutomationConditionType.PhasedReleaseDay) {
                                    const threshold = condition.threshold ?? thresholds.phasedReleaseDayThreshold;

                                    if (!threshold) {
                                        text = '';
                                    } else {
                                        text = `Day ${threshold} of phased release`;
                                    }
                                } else if (
                                    condition.type === MonitoringAutomationConditionType.StagedRolloutPercentage
                                ) {
                                    const threshold =
                                        condition.threshold ?? thresholds.stagedRolloutPercentageThreshold;

                                    if (!threshold) {
                                        text = '';
                                    } else {
                                        text = `${threshold}% staged rollout percentage`;
                                    }
                                } else if (condition.type === MonitoringAutomationConditionType.HealthMetric) {
                                    const monitoringMetric =
                                        condition.metricId && monitoringMetricsById[condition.metricId];
                                    text =
                                        monitoringMetric ?
                                            displayTextForHealthMetricPrecondition(monitoringMetric)
                                        :   '';
                                }

                                return (
                                    <Space key={conditionIndex}>
                                        {condition.isConditionMet ?
                                            <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />
                                        :   <ClockCircleFilled style={{ color: antGrey.grey6 }} />}
                                        <Text style={conditionTextStyle}>{text}</Text>
                                    </Space>
                                );
                            })}
                            {checklistItems?.map((cliCondition, cliIndex) => {
                                const condition = props.checklistItems?.find((cli) => cli.id === cliCondition.id);
                                if (!condition) {
                                    return null;
                                }
                                const isCliCompleted =
                                    condition?.status &&
                                    (condition?.status?.status === ChecklistItemStatusType.Approved ||
                                        condition?.status?.status === ChecklistItemStatusType.Passed);
                                const selectedConditionOwners = getRolloutChecklistItemsOwners(
                                    condition,
                                    orgUsers,
                                    orgGroups,
                                );
                                const text =
                                    selectedConditionOwners.length === 0 ?
                                        condition?.title
                                    :   `${condition?.title} (owned by ${selectedConditionOwners.join(', ')})`;
                                return (
                                    <Space key={cliIndex}>
                                        {isCliCompleted ?
                                            <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />
                                        :   <ClockCircleFilled style={{ color: antGrey.grey6 }} />}
                                        <Text style={conditionTextStyle}>{text}</Text>
                                    </Space>
                                );
                            })}
                        </>
                    );

                    const validAssociatedMetrics = metricsWithThresholds(automation.associatedMetrics);

                    return (
                        <PlanRestrictedFeatureWrapper
                            key={index}
                            feature={SubscriptionFeatureType.Rollouts}
                            shouldFakeDisabledState
                        >
                            <Collapse
                                bordered={false}
                                expandIcon={({ isActive }) => (
                                    <CaretRightOutlined
                                        style={{
                                            color: antGrey.grey6,
                                            fontSize: 18,
                                        }}
                                        rotate={isActive ? 90 : 0}
                                    />
                                )}
                                style={{
                                    backgroundColor: antGrey.grey2,
                                    borderRadius: 8,
                                }}
                            >
                                <Collapse.Panel
                                    key="1"
                                    header={
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                width: '100%',
                                            }}
                                        >
                                            <Space>
                                                <ThunderboltFilled
                                                    style={{
                                                        color:
                                                            automationEnabledAndReleaseNotSkipped ?
                                                                runwayColors.primary
                                                            :   antGrey.grey6,
                                                    }}
                                                />
                                                <Text
                                                    style={{
                                                        color:
                                                            automationEnabledAndReleaseNotSkipped ?
                                                                runwayColors.darkTextGrey
                                                            :   antGrey.grey6,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {description}
                                                </Text>
                                                <Link to={automationLink} onClick={(e) => e.stopPropagation()}>
                                                    <SettingOutlined style={{ color: runwayColors.primary }} />
                                                </Link>
                                            </Space>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                {statusTagForAutomationStatus(
                                                    automationFromSettings.enabled,
                                                    automationFromSettings.type,
                                                    status,
                                                    statusMessage,
                                                    automation.triggeredAt,
                                                    automation.discardedAt,
                                                )}
                                            </div>
                                        </div>
                                    }
                                    style={{
                                        backgroundColor: antGrey.grey2,
                                        border: 'none',
                                    }}
                                    className="automations-panel"
                                >
                                    {!automationFromSettings.enabled && (
                                        <div
                                            style={{
                                                backgroundColor: colorWithAlpha(rwColors.primary, 0.2),
                                                borderRadius: 5,
                                                padding: 10,
                                                marginBottom: 16,
                                            }}
                                        >
                                            <Text style={conditionTextStyle}>
                                                Go to <Link to={automationLink}>App settings &gt; Automations</Link> to
                                                enable this automation.
                                            </Text>
                                        </div>
                                    )}
                                    {props.shouldShowDisabledUI ?
                                        <div
                                            style={{
                                                backgroundColor: runwayColors.white,
                                                padding: 8,
                                                borderRadius: 4,
                                                width: '100%',
                                            }}
                                        >
                                            <Text
                                                style={{
                                                    color: antGrey.grey7,
                                                    fontSize: 12,
                                                }}
                                            >
                                                Rollout automations not available on Free plan.
                                            </Text>
                                        </div>
                                    :   <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(3, 1fr)',
                                                gap: 16,
                                                width: '100%',
                                            }}
                                        >
                                            {shouldShowMetricsData &&
                                                (hasConditionGroups ?
                                                    <div
                                                        style={{
                                                            gridColumn: '1 / -1',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 12,
                                                        }}
                                                    >
                                                        {conditionGroups.map((group, groupIndex) => (
                                                            <div
                                                                key={group.id ?? groupIndex}
                                                                style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 8,
                                                                }}
                                                            >
                                                                {groupIndex > 0 && (
                                                                    <Text
                                                                        style={{
                                                                            ...conditionTextStyle,
                                                                            fontStyle: 'italic',
                                                                        }}
                                                                    >
                                                                        or
                                                                    </Text>
                                                                )}
                                                                {conditionGroups.length > 1 && (
                                                                    <Text
                                                                        style={{
                                                                            color: antGrey.grey8,
                                                                            fontSize: 12,
                                                                            fontWeight: 600,
                                                                        }}
                                                                    >
                                                                        {group.name}
                                                                    </Text>
                                                                )}
                                                                <div
                                                                    style={{
                                                                        display: 'grid',
                                                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                                                        gap: 16,
                                                                        width: '100%',
                                                                    }}
                                                                >
                                                                    <ConditionsOrMetricsContainer title="Health metrics">
                                                                        {renderHealthMetrics(
                                                                            metricsWithThresholds(
                                                                                group.associatedMetrics,
                                                                            ),
                                                                        )}
                                                                    </ConditionsOrMetricsContainer>
                                                                    <ConditionsOrMetricsContainer title="Conditions">
                                                                        {renderConditions(
                                                                            group.associatedConditions,
                                                                            thresholdsForConditionGroup(group.id),
                                                                            [],
                                                                        )}
                                                                    </ConditionsOrMetricsContainer>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                :   <>
                                                        <ConditionsOrMetricsContainer title="Health metrics">
                                                            {renderHealthMetrics(validAssociatedMetrics)}
                                                        </ConditionsOrMetricsContainer>
                                                        <ConditionsOrMetricsContainer title="Conditions">
                                                            {renderConditions(
                                                                automation.associatedConditions,
                                                                automationThresholds,
                                                                automation.checklistItems,
                                                            )}
                                                        </ConditionsOrMetricsContainer>
                                                    </>)}
                                            {shouldShowAutomationSteps && (
                                                <ConditionsOrMetricsContainer title="Steps">
                                                    {automation.steps.map((step, stepIndex) => {
                                                        return (
                                                            <Space key={stepIndex}>
                                                                {iconForAutomationStepStatus(step.status)}
                                                                <Text
                                                                    style={{
                                                                        ...conditionTextStyle,
                                                                    }}
                                                                >
                                                                    {step.description}
                                                                </Text>
                                                            </Space>
                                                        );
                                                    })}
                                                </ConditionsOrMetricsContainer>
                                            )}
                                            {shouldShowRollbackDiscardedText && (
                                                <Text type="secondary">
                                                    The prepared rollback was discarded by{' '}
                                                    {automation.discardedAutomaticallyByRunway === true ?
                                                        'Runway'
                                                    :   `${automation.discardedBy}`}{' '}
                                                    at{' '}
                                                    {dayjs(automation.discardedAt)
                                                        .tz(effectiveTimezone)
                                                        .format(DatetimeFormat.TimeShortDate)}
                                                    .
                                                </Text>
                                            )}
                                        </div>
                                    }
                                </Collapse.Panel>
                            </Collapse>
                        </PlanRestrictedFeatureWrapper>
                    );
                })}
            </div>
        </div>
    );
};

export default MonitoringAutomations;

const iconForMonitoringMetricStatus: (status?: MonitoringMetricHealthStatus) => React.ReactNode = (status) => {
    switch (status) {
        case MonitoringMetricHealthStatus.Healthy:
            return <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />;
        case MonitoringMetricHealthStatus.Unhealthy:
            return <CloseCircleFilled style={{ color: runwayColors.statusRed }} />;
        default:
            return <ClockCircleFilled style={{ color: antGrey.grey6 }} />;
    }
};

const ConditionsOrMetricsContainer: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
    const settingsHeaderStyle: React.CSSProperties = {
        textTransform: 'uppercase',
        color: antGrey.grey7,
        fontSize: 10,
        letterSpacing: 1.1,
    };

    return (
        <div
            style={{
                backgroundColor: runwayColors.white,
                borderRadius: 8,
                border: `1px solid ${antGrey.grey5}`,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                flex: 1,
            }}
        >
            <Text style={settingsHeaderStyle}>{title}</Text>
            {children}
        </div>
    );
};

const iconForAutomationStepStatus: (status: AutomationStatus) => React.ReactNode = (status) => {
    const color = colorForAutomationStatus(status, false);

    switch (status) {
        case AutomationStatus.Success:
            return <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />;
        case AutomationStatus.Pending:
            return <ClockCircleFilled style={{ color: antGrey.grey6 }} />;
        case AutomationStatus.Failed:
            return <CloseCircleFilled style={{ color: runwayColors.statusRed }} />;
        default:
            return iconForAutomationStatus(status, color, 16);
    }
};
